import React, { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, CheckCheck, Download, Send, Settings, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { userApi } from '@/lib/api';
import { canInstall, disablePush, enablePush, installApp, isIos, isStandalone, pushCapability, syncExistingPushSubscription, updateAppBadge } from '@/lib/pwa';

const preferenceLabels = {
  financial: 'النشاط المالي', approvals: 'الموافقات', transfers: 'التحويلات', goals: 'الأهداف والصناديق', subscription: 'الاشتراك', announcements: 'إعلانات البنك'
};

export default function NotificationCenter({ notifications, setNotifications, onNavigate }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [installAvailable, setInstallAvailable] = useState(canInstall());

  const loadStatus = useCallback(async () => {
    const capability = pushCapability();
    try {
      const { data } = await userApi.get('/user/push/status');
      const local = await syncExistingPushSubscription().catch(() => null);
      setStatus({ ...data, ...capability, enabledOnDevice: Boolean(local) });
    } catch {
      setStatus({ ...capability, configured: false, preferences: {} });
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    const refresh = () => setInstallAvailable(canInstall());
    window.addEventListener('mb:install-available', refresh);
    window.addEventListener('mb:installed', refresh);
    return () => { window.removeEventListener('mb:install-available', refresh); window.removeEventListener('mb:installed', refresh); };
  }, []);

  const unread = notifications.filter(item => !item.is_read).length;

  const markRead = async item => {
    if (!item.is_read) await userApi.post(`/user/notifications/${encodeURIComponent(item.id)}/read`);
    const next = notifications.map(row => row.id === item.id ? { ...row, is_read: true } : row);
    setNotifications(next);
    updateAppBadge(next.filter(row => !row.is_read).length);
    if (item.target_url) {
      const target = new URL(item.target_url, window.location.origin);
      const tab = target.searchParams.get('tab');
      window.history.replaceState({}, '', `${target.pathname}${target.search}`);
      if (tab && tab !== 'notifications') onNavigate(tab);
    }
  };

  const markAll = async () => {
    await userApi.post('/user/notifications/read-all');
    setNotifications(items => items.map(item => ({ ...item, is_read: true })));
    updateAppBadge(0);
  };

  const remove = async (event, item) => {
    event.stopPropagation();
    await userApi.delete(`/user/notifications/${encodeURIComponent(item.id)}`);
    const next = notifications.filter(row => row.id !== item.id);
    setNotifications(next);
    updateAppBadge(next.filter(row => !row.is_read).length);
  };

  const togglePush = async () => {
    setBusy(true);
    try {
      if (status?.enabledOnDevice) {
        await disablePush();
        toast.success('تم تعطيل الإشعارات على هذا الجهاز.');
      } else {
        await enablePush();
        toast.success('تم تفعيل إشعارات بنك العائلة على هذا الجهاز.');
      }
      await loadStatus();
    } catch (error) {
      if (error.message === 'IOS_INSTALL_REQUIRED') toast.info('أضف بنك العائلة إلى الشاشة الرئيسية أولًا، ثم افتحه من الأيقونة لتفعيل الإشعارات.');
      else if (error.message === 'PERMISSION_DENIED') toast.error('تم حظر الإشعارات من إعدادات المتصفح. يمكنك السماح بها من إعدادات الموقع.');
      else if (error.message === 'PUSH_NOT_CONFIGURED') toast.error('مفاتيح Push غير مفعّلة على الخادم بعد.');
      else toast.error('تعذر تغيير حالة الإشعارات على هذا الجهاز.');
      await loadStatus();
    } finally { setBusy(false); }
  };

  const savePreference = async (key, value) => {
    const next = { ...(status?.preferences || {}), [key]: value };
    setStatus(current => ({ ...current, preferences: next }));
    try { await userApi.put('/user/notification-preferences', next); }
    catch { toast.error('تعذر حفظ تفضيلات الإشعارات.'); await loadStatus(); }
  };

  const testPush = async () => {
    setBusy(true);
    try { await userApi.post('/user/push/test'); toast.success('تم إرسال إشعار تجريبي.'); }
    catch { toast.error('تعذر إرسال الإشعار التجريبي.'); }
    finally { setBusy(false); }
  };

  return <div className="space-y-5">
    <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-secondary)]/40 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h3 className="flex items-center gap-2 font-black"><Settings className="h-4 w-4" /> إشعارات الجهاز</h3><p className="mt-1 text-xs text-[var(--c-muted)]">
          {!status?.supported ? 'Push Notifications غير متاحة على هذا الجهاز.' : status?.permission === 'denied' ? 'الإشعارات محظورة من إعدادات المتصفح.' : status?.iosNeedsInstall ? 'للحصول على إشعارات بنك العائلة، أضف التطبيق إلى الشاشة الرئيسية أولًا.' : status?.enabledOnDevice ? 'مفعّلة على هذا الجهاز.' : 'غير مفعّلة على هذا الجهاز.'}
        </p></div>
        {status?.supported && !status?.iosNeedsInstall && <button disabled={busy || status?.permission === 'denied'} onClick={togglePush} className="btn-navy flex items-center gap-2 disabled:opacity-50">{status?.enabledOnDevice ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}{status?.enabledOnDevice ? 'تعطيل' : 'تفعيل'}</button>}
      </div>
      {status?.enabledOnDevice && <button disabled={busy} onClick={testPush} className="mt-3 flex items-center gap-2 text-xs font-bold text-blue-700"><Send className="h-3.5 w-3.5" /> إرسال إشعار تجريبي</button>}
      {isIos() && !isStandalone() && <div className="mt-3 rounded-xl bg-blue-50 p-3 text-xs leading-6 text-blue-900">على iPhone/iPad: افتح زر المشاركة في Safari، اختر «إضافة إلى الشاشة الرئيسية»، ثم افتح بنك العائلة من الأيقونة.</div>}
      {(installAvailable || (isIos() && !isStandalone())) && <button onClick={async()=>{if(installAvailable){await installApp();setInstallAvailable(canInstall());}}} className="mt-3 flex items-center gap-2 text-xs font-black text-[var(--c-navy)]"><Download className="h-4 w-4" />{installAvailable ? 'تثبيت بنك العائلة' : 'أضف بنك العائلة إلى الشاشة الرئيسية'}</button>}
      {status?.preferences && <div className="mt-4 grid gap-2 sm:grid-cols-2">{Object.entries(preferenceLabels).map(([key,label])=><label key={key} className="flex min-h-10 items-center justify-between gap-3 rounded-xl border bg-white px-3 text-xs font-bold"><span>{label}</span><input type="checkbox" checked={status.preferences[key] !== false} onChange={event=>savePreference(key,event.target.checked)} /></label>)}</div>}
    </section>

    <section>
      <div className="mb-3 flex items-center justify-between gap-3"><div><h3 className="font-black">مركز الإشعارات</h3><p className="text-xs text-[var(--c-muted)]">{unread} غير مقروء</p></div>{unread > 0 && <button onClick={markAll} className="flex items-center gap-1 text-xs font-bold text-blue-700"><CheckCheck className="h-4 w-4" /> قراءة الكل</button>}</div>
      <div className="max-h-80 space-y-2 overflow-auto">
        {notifications.length === 0 ? <p className="py-8 text-center text-sm text-[var(--c-muted)]">لا توجد إشعارات.</p> : notifications.map(item => <button key={item.id} onClick={()=>markRead(item)} className={`group flex w-full items-start gap-3 rounded-xl border p-3 text-start ${item.is_read ? 'border-slate-200 bg-white' : 'border-amber-200 bg-amber-50'}`}>
          <div className="min-w-0 flex-1"><div className="text-sm font-black">{item.title || 'بنك العائلة'}</div><div className="mt-1 text-xs leading-5 text-[var(--c-muted)]">{item.body || item.message}</div><div className="mt-1 text-[10px] text-slate-500">{new Date(item.created_at).toLocaleString('ar-LB')}</div></div>
          {item.deletable !== false && <span role="button" tabIndex={0} onClick={event=>remove(event,item)} className="rounded-lg p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></span>}
        </button>)}
      </div>
    </section>
  </div>;
}
